/**
 * Pattern registry + selector. The twelve attack patterns register here; the
 * world scheduler asks {@link selectPattern} which one fires next.
 *
 * Selection improves on legacy Avoid! (uniform `Math.ceil(random*11)+1`, repeats
 * allowed — see docs/legacy/AVOID_LEGACY_ANALYSIS.md §1.3) with two rules:
 *  1. difficulty-weighted (harder tiers favour the nastier families), and
 *  2. no-immediate-repeat of the previous family.
 * Both are driven by the seeded `rng`, so selection stays reproducible for
 * anti-cheat re-simulation and replays.
 *
 * NOTE: pure core module — never import Phaser, React, or Firebase here.
 */
import type { AttackFamily, PatternId, Rng } from '../types';
import type { DifficultyTier } from '../constants';
import type { Pattern } from './types';
import { WALL_LR, WALL_TB } from './walls';
import { TURRET } from './turret';
import {
  LASER_WALL_1,
  LASER_WALL_2,
  LASER_WALL_3,
  LASER_WALL_4,
  LASER_SWEEP_LR,
  LASER_SWEEP_RL,
} from './laserWalls';
import { CARO } from './caro';
import { BOMB_LEFT, BOMB_RIGHT } from './bombs';
import { MISSILE_LR, MISSILE_RL } from './missiles';

/**
 * The registry of all attack patterns, legacy 1–12 first (in their fixed order;
 * docs/legacy/AVOID_LEGACY_ANALYSIS.md §1.3) then the added blinking sweep lasers.
 * The order is fixed so the seeded weighted draw in {@link selectPattern} is
 * reproducible across client and server.
 */
export const PATTERNS: readonly Pattern[] = [
  WALL_LR, // 1  — twin crushers, left + right
  WALL_TB, // 2  — twin crushers, top + bottom
  TURRET, // 3
  LASER_WALL_1, // 4
  LASER_WALL_2, // 5
  LASER_WALL_3, // 6
  LASER_WALL_4, // 7
  CARO, // 8
  BOMB_LEFT, // 9
  BOMB_RIGHT, // 10
  MISSILE_LR, // 11
  MISSILE_RL, // 12
  LASER_SWEEP_LR, // 13
  LASER_SWEEP_RL, // 14
] as const;

/**
 * Family weighting by difficulty level. Each entry is `base + perLevel * level`, so
 * the "nastier" families (turret, caro, missiles — fast, hard-to-read, or lethal the
 * whole flight) gain share as the run escalates, while the readable early-game
 * families (walls, simple laser walls, lobbed bombs) keep a steady floor. This is
 * the difficulty bias the legacy uniform draw lacked.
 */
const FAMILY_WEIGHTS: Record<AttackFamily, { base: number; perLevel: number }> = {
  // walls spans 2 patterns (LR / TB); selection is per-pattern.
  walls: { base: 6, perLevel: 0 },
  // lasers now spans 6 patterns (4 walls + 2 sweeps); the per-pattern weight is
  // trimmed so the FAMILY share stays close to before (selection is per-pattern).
  lasers: { base: 3.3, perLevel: 0.35 },
  turret: { base: 2, perLevel: 1.5 },
  caro: { base: 2, perLevel: 1.25 },
  missiles: { base: 2, perLevel: 1.0 },
  bombs: { base: 4, perLevel: 0.25 },
};

/** The weight of a single pattern at a given difficulty level (always > 0). */
function patternWeight(pattern: Pattern, level: number): number {
  const w = FAMILY_WEIGHTS[pattern.family];
  return Math.max(0.01, w.base + w.perLevel * level);
}

/**
 * Pick the next pattern to fire. Difficulty-weighted and never repeats the
 * previous family (`lastFamily`); deterministic given `rng`.
 *
 * The draw is a single seeded sample over the weighted, family-filtered candidate
 * set — so it advances `rng` exactly once per call regardless of difficulty, keeping
 * the stream position stable as tuning changes (important for reproducible runs).
 *
 * @param rng        the run's seeded stream (advances exactly once per call).
 * @param difficulty the active tier — biases the weighting.
 * @param lastFamily the family that fired last (or null on the first attack),
 *                   excluded from the draw to avoid back-to-back repeats.
 * @returns the chosen {@link Pattern}.
 */
export function selectPattern(
  rng: Rng,
  difficulty: DifficultyTier,
  lastFamily: AttackFamily | null,
): Pattern {
  // Exclude the previous family so the same family never fires twice in a row. On
  // the first attack (lastFamily === null) nothing is excluded.
  const candidates = PATTERNS.filter((p) => p.family !== lastFamily);
  // PATTERNS is non-empty and at most one family is excluded, so candidates is never
  // empty; the fallback keeps the return type honest for the type-checker.
  const pool = candidates.length > 0 ? candidates : PATTERNS;

  const weights = pool.map((p) => patternWeight(p, difficulty.level));
  const total = weights.reduce((sum, w) => sum + w, 0);

  // Single seeded sample across the cumulative weight axis.
  let roll = rng() * total;
  for (let i = 0; i < pool.length; i++) {
    roll -= weights[i];
    if (roll < 0) return pool[i];
  }
  // Floating-point guard: if rounding nudges `roll` past the end, take the last.
  return pool[pool.length - 1];
}

/** Look up a pattern by its {@link PatternId} (e.g. to re-spawn a specific attack). */
export function patternById(id: PatternId): Pattern | undefined {
  return PATTERNS.find((p) => p.id === id);
}
